import {Navigate, useParams, useNavigate} from 'react-router-dom'
import {useCallback, useEffect, useState} from 'react'
import css from './WorkspaceProjectPage.module.css'
import {ListsProvider, useLists} from '../../../contexts/ListsContext'
import {TasksProvider} from '../../../contexts/TasksContext'
import {useAppSelector, useAppDispatch} from '../../../store/hooks'
import {selectAuth} from '../../../store/auth/authSelectors'
import {openConfirmDialog, setOpenDropdown, setOpenModal} from '../../../store/ui/uiSlice'
import {selectProjectById, selectProjectsLoading} from '../../../store/projects/projectsSelectors'
import {selectCanDeleteProjectList, selectCanProjectOption} from '../../../store/workspace/workspaceSelectors'
import {
    updateProjectNameSaga,
    updateProjectDescriptionSaga,
    deleteProjectSaga,
    publishProjectSaga
} from '../../../store/projects/projectsSlice'
import {markProjectWatchedSaga} from '../../../store/notifications/notificationsSlice'
import type {Project} from '../../../store/projects/projectsSlice'
import {uploadAttachment} from '../../../lib/uploadAttachment'
import WorkspaceProjectTitle from './components/WorkspaceProjectTitle/WorkspaceProjectTitle'
import WorkspaceProjectDescription from './components/WorkspaceProjectDescription/WorkspaceProjectDescription'
import WorkspaceProjectLists from './components/WorkspaceProjectLists/WorkspaceProjectLists'
import WorkspacePrivateProjectPage from '../WorkspacePrivateProjectPage/WorkspacePrivateProjectPage'
import DropdownWrapper from "@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper.tsx";
import Dropdown from "../../ui/Dropdown/Dropdown.tsx";
import {selectOpenDropdownId} from "../../../store/ui/uiSelectors.ts";
import DropdownItem from "@/components/ui/Dropdown/components/DropdownItem/DropdownItem.tsx";
import IconButton from '@/components/ui/IconButton/IconButton'
import OptionsIcon from '@/assets/icons/icon-options.svg?react'
import PlusIcon from '../../../assets/icons/icon-plus.svg?react'
import IconCopy from '@/assets/icons/icon-copy.svg?react'
import DropdownDivider from "@/components/ui/Dropdown/components/DropdownDivider/DropdownDivider.tsx";
import Button from "@/components/ui/Button/Button.tsx";
import PlateLoader from '@/components/ui/Loader/PlateLoader'
import { showToast } from '@/components/app/Toast/showToast'

const DROPDOWN_ID = 'workspace-project-title'

const WorkspaceProjectPage = () => {
    const {projectId} = useParams<{ projectId: string }>()
    const loading = useAppSelector(selectProjectsLoading)
    const project = useAppSelector(state => selectProjectById(state, projectId!))
    const auth = useAppSelector(selectAuth)

    if (loading) {
        // Cold direct link to a project: projects haven't loaded yet (the splash now drops on
        // shell-ready). Show the in-content loader instead of a blank area (SCD-893).
        return <PlateLoader fullscreen />
    }

    // Privacy check only for authenticated users. Guests in local-only mode
    // cannot land in a foreign workspace/project, so skip the check.
    if (!project && auth) {
        return <WorkspacePrivateProjectPage/>
    }

    if (!project) {
        return <Navigate to="/" replace/>
    }

    return (
        <ListsProvider projectId={projectId}>
            <TasksProvider projectId={projectId}>
                <WorkspaceProjectPageContent project={project}/>
            </TasksProvider>
        </ListsProvider>
    )
}

interface WorkspaceProjectPageContentProps {
    project: Project
}

const WorkspaceProjectPageContent = ({project}: WorkspaceProjectPageContentProps) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const {workspace: workspaceUrlId} = useParams<{ workspace: string }>()
    const {createList} = useLists()
    const authUser = useAppSelector(selectAuth)
    const canDeleteProjectList = useAppSelector(selectCanDeleteProjectList)
    const openDropdownId = useAppSelector(selectOpenDropdownId)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const canProjectOption = useAppSelector(selectCanProjectOption)

    useEffect(() => {
        dispatch(markProjectWatchedSaga(project.id))
    }, [project.id, dispatch])

    useEffect(() => {
        const shouldBeOpen = openDropdownId === DROPDOWN_ID
        if (isDropdownOpen !== shouldBeOpen) {
            setIsDropdownOpen(shouldBeOpen)
        }
    }, [openDropdownId, isDropdownOpen])

    const handleUpdateName = (name: string) => {
        dispatch(updateProjectNameSaga({projectId: project.id, name}))
    }

    const handleUpdateDescription = (description: string) => {
        dispatch(updateProjectDescriptionSaga({projectId: project.id, description}))
    }

    const handleImageUpload = useCallback(async (file: File) => {
        if (!authUser) throw new Error('Not authenticated')
        return uploadAttachment({file, workspaceId: project.workspaceId, userId: authUser.id, projectId: project.id})
    }, [project.workspaceId, project.id, authUser])

    const handleSignIn = () => {
        navigate('/auth')
    }

    const handleDelete = () => {
        const projectName = project.name || 'Untitled project'
        dispatch(openConfirmDialog({
            title: 'Delete project',
            text: `Are you sure you want to delete "${projectName}"? This will also delete all lists and tasks in this project. This cannot be undone.`,
            confirmButtonTitle: 'Delete',
            confirmButtonAction: async () => {
                dispatch(deleteProjectSaga(project.id))
            }
        }))
    }

    const handleDropdownClick = () => {
        dispatch(setOpenDropdown(isDropdownOpen ? null : DROPDOWN_ID))
    }

    const handleDropdownClose = () => {
        dispatch(setOpenDropdown(null))
    }

    const handleAddList = async () => {
        dispatch(setOpenDropdown(null))
        await createList(project.id, '')
    }

    const handleDeleteDropdown = () => {
        dispatch(setOpenDropdown(null))
        handleDelete()
    }

    const handleCopyLink = () => {
        const url = `${window.location.origin}/${workspaceUrlId}/projects/${project.id}`
        navigator.clipboard.writeText(url)
        showToast('Link copied')
        dispatch(setOpenDropdown(null))
    }

    const handleMembers = () => {
        dispatch(setOpenDropdown(null))
        dispatch(setOpenModal({ type: 'projectMembers', projectId: project.id }))
    }

    const handleActivityLog = () => {
        dispatch(setOpenDropdown(null))
        dispatch(setOpenModal({ type: 'projectActivity', projectId: project.id }))
    }

    const handlePublish = () => {
        dispatch(setOpenDropdown(null))
        dispatch(publishProjectSaga(project.id))
    }

    const handleShareLink = () => {
        dispatch(setOpenDropdown(null))
        dispatch(setOpenModal({ type: 'publishProject', projectId: project.id }))
    }

    return (
        <div className={css.container}>
            <div className={css.header_top_right}>
                {canProjectOption ? <DropdownWrapper>
                        <div className={css.options_btn}>
                            <IconButton grey={true} icon={<OptionsIcon/>} onClick={handleDropdownClick} testId="project-options-btn"/>
                            {project.isPublic && <span className={css.published_dot}/>}
                        </div>
                        <Dropdown align={"right"} isOpen={isDropdownOpen} onClose={handleDropdownClose}>
                            <DropdownItem
                                icon={<IconCopy/>}
                                title="Copy link"
                                onClick={handleCopyLink}
                            />
                            <DropdownDivider/>
                            <DropdownItem
                                icon={<PlusIcon/>}
                                title="Add section"
                                onClick={handleAddList}
                            />
                            <DropdownDivider/>
                            <DropdownItem
                                title="Members"
                                onClick={handleMembers}
                            />
                            <DropdownItem
                                title="Activity"
                                onClick={handleActivityLog}
                            />
                            {project.isPublic ? (
                                <DropdownItem
                                    title="Public link"
                                    onClick={handleShareLink}
                                    rightIcon={<span className={css.menu_dot}/>}
                                />
                            ) : (
                                <DropdownItem
                                    title="Public link"
                                    onClick={handlePublish}
                                />
                            )}
                            {canDeleteProjectList && (
                                <>
                                    <DropdownDivider/>
                                    <DropdownItem
                                        danger={true}
                                        title="Delete"
                                        onClick={handleDeleteDropdown}
                                        testId="project-delete-btn"
                                    />
                                </>
                            )}
                        </Dropdown>
                    </DropdownWrapper> :
                    <>
                        <Button onClick={handleSignIn} type={"black"} title={"Sign in"} />
                    </>}
            </div>
            <WorkspaceProjectTitle project={project} onUpdateName={handleUpdateName}/>
            <WorkspaceProjectDescription project={project} onUpdateDescription={handleUpdateDescription}
                                         onFileUpload={authUser ? handleImageUpload : undefined}/>
            <WorkspaceProjectLists/>
        </div>
    )
}

export default WorkspaceProjectPage
